import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
} from "@mui/material";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import SelectableInfluencerTile from "./SelectableInfluencerTile";
import { fetchInfModels } from "../../influencers/infModelsApi";

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   lang?: object;
 *   selectedCharacter?: object | null;
 *   onConfirm: (character: object) => void;
 * }} props
 */
export default function PickCharacterModal({
  open,
  onClose,
  lang,
  selectedCharacter,
  onConfirm,
}) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [loading, setLoading] = React.useState(false);
  const [characters, setCharacters] = React.useState([]);
  const [pick, setPick] = React.useState(null);

  const loadCharacters = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchInfModels(hitAxios);
      if (!res?.data?.success) {
        throw new Error(res?.data?.msg || "Could not load characters.");
      }
      const rows = (res.data.data || []).filter(
        (inf) => inf.status === "active" && inf.photo_url,
      );
      setCharacters(rows);
    } catch (err) {
      showSnack?.(
        err?.message || lang?.socialCharactersLoadFailed || "Could not load characters.",
        false,
      );
      setCharacters([]);
    } finally {
      setLoading(false);
    }
  }, [hitAxios, lang, showSnack]);

  React.useEffect(() => {
    if (!open) return;
    setPick(selectedCharacter || null);
    loadCharacters();
  }, [open, selectedCharacter, loadCharacters]);

  const handleConfirm = () => {
    if (!pick?.id || !pick?.photo_url) return;
    onConfirm(pick);
    onClose();
  };

  const influencersUrl = "/user?page=influencers";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
        <PersonOutlined color="primary" />
        {lang?.socialPickCharacterTitle || "Choose character"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            {lang?.socialPickCharacterHint ||
              "Select an AI Character image from your library (same list as the Characters page)."}
          </Alert>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : characters.length === 0 ? (
            <Alert severity="warning">
              {lang?.socialCharactersEmpty || "No active characters with photos yet."}{" "}
              <Link href={influencersUrl} underline="hover">
                {lang?.socialGoToCharacters || "Create characters"}
              </Link>
            </Alert>
          ) : (
            <Grid container spacing={1.5}>
              {characters.map((inf) => (
                <Grid item xs={6} sm={4} md={3} key={inf.id}>
                  <SelectableInfluencerTile
                    influencer={inf}
                    selected={pick?.id === inf.id}
                    onSelect={() => setPick(inf)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          disabled={!pick?.id || !pick?.photo_url}
          onClick={handleConfirm}
          sx={{ fontWeight: 800 }}
        >
          {lang?.socialUseCharacter || "Use this character"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
